import React from 'react';
import { Check, Sparkles } from 'lucide-react';
import { trackVideoView } from '../utils/meta-pixel';

interface Feature {
  id: number;
  title: string;
  description: string;
  benefits: string[];
}

interface VimeoVideo {
  src: string;
  title: string;
}

interface VimeoEmbedProps {
  src: string;
  title: string;
}

function buildVimeoSrc(videoId: string): string {
  const params = new URLSearchParams({
    badge: '0',
    autopause: '0',
    player_id: '0',
    app_id: '58479',
    autoplay: '1',
    muted: '1',
    loop: '1',
    controls: '0',
    title: '0',
    byline: '0',
    portrait: '0',
    background: '1',
  });

  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
}

const vimeoVideos: Record<number, VimeoVideo> = {
  1: {
    src: buildVimeoSrc('1157295156'),
    title: '1-extension',
  },
  2: {
    src: buildVimeoSrc('1157295190'),
    title: '2-click2call',
  },
  3: {
    src: buildVimeoSrc('1157295277'),
    title: '5-ai',
  },
  4: {
    src: buildVimeoSrc('1157295222'),
    title: '3-users',
  },
  5: {
    src: buildVimeoSrc('1157295240'),
    title: '4-invites',
  },
};

const VimeoEmbed = React.memo(function VimeoEmbed({ src, title }: VimeoEmbedProps) {
  return (
    <iframe
      className="absolute inset-0 h-full w-full"
      src={src}
      title={title}
      frameBorder={0}
      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
      loading="eager"
      tabIndex={-1}
    />
  );
});

interface VideoCardProps {
  title: string;
  id: number;
}

const VideoCard = React.memo(function VideoCard({ title, id }: VideoCardProps) {
  const vimeoVideo = vimeoVideos[id];
  const videoSrc = vimeoVideo?.src ?? '';
  const videoTitle = vimeoVideo?.title ?? title;
  const [videoViewTracked, setVideoViewTracked] = React.useState(false);

  const handleVideoAreaClick = () => {
    if (!videoViewTracked) {
      trackVideoView(title);
      setVideoViewTracked(true);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-gray-200 w-full max-w-2xl relative">
      {/* Video Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded-full opacity-30" />
          <div className="w-3 h-3 bg-yellow-500 rounded-full opacity-30" />
          <div className="w-3 h-3 bg-blue-500 rounded-full opacity-30" />
        </div>
      </div>

      {/* Video */}
      <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
        <div className="relative w-full pt-[56.25%]">
          {videoSrc ? (
            <>
              <VimeoEmbed src={videoSrc} title={videoTitle} />
              {!videoViewTracked && (
                <div
                  className="absolute inset-0 z-10 cursor-pointer"
                  onClick={handleVideoAreaClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleVideoAreaClick();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Assistir vídeo: ${title}`}
                />
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
                Vídeo indisponível
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const Features: React.FC = () => {
  const [mousePos, setMousePos] = React.useState({ x: 0, y: 0 });
  const sectionRef = React.useRef<HTMLElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const features: Feature[] = [
    {
      id: 1,
      title: "Extensão direto no navegador",
      description: "Instale, faça login com 1 clique e comece a ligar para qualquer lugar do Brasil direto do navegador — sem setup demorado e sem softphone.",
      benefits: [
        "Login em 1 clique",
        "Discagem direto no navegador",
        "Sem softphone e sem complicação",
        "Time operando em minutos"
      ]
    },
    {
      id: 2,
      title: "Click2Call (detecção de número)",
      description: "O Onflow identifica números de telefone na sua tela (ex.: WhatsApp) e adiciona um ícone para você ligar com 1 clique.",
      benefits: [
        "Clique para ligar onde o número aparecer",
        "Menos copia/cola e menos erro humano",
        "Aumenta ritmo de discagem",
        "Fluxo mais natural para o operador"
      ]
    },
    {
      id: 3,
      title: "Análise e transcrição por IA",
      description: "Transcreva chamadas e receba um relatório objetivo: desempenho, feedback e nota do atendimento para treinar e padronizar o time.",
      benefits: [
        "Transcrição automática por chamada",
        "Relatório com pontos de melhoria",
        "Feedback rápido para o operador",
        "Nota/score do atendimento"
      ]
    },
    {
      id: 4,
      title: "Gestão e métricas em tempo real",
      description: "Acompanhe ao vivo desempenho, ociosidade, status e monitoria das ligações — com visibilidade total para agir rápido e padronizar o time.",
      benefits: [
        "Métricas e status em tempo real",
        "Tempo de ociosidade e produtividade por operador",
        "Escuta de ligações para suporte e QA",
        "Visão do time em um painel"
      ]
    },
    {
      id: 5,
      title: "Convide seu time (usuários ilimitados)",
      description: "Convide seu time em poucos cliques, sem limite de usuários, e coloque todo mundo para operar rápido — sem burocracia e sem fricção.",
      benefits: [
        "Usuários ilimitados",
        "Convite rápido e onboarding simples",
        "Time operando em minutos",
        "Expanda a operação sem travas"
      ]
    }
  ];

  const ConnectionLine: React.FC<{ isReversed: boolean }> = ({ isReversed }) => (
    <div 
      className={`hidden lg:block absolute top-1/2 transform -translate-y-1/2 pointer-events-none z-0`}
      style={{
        [isReversed ? 'right' : 'left']: '100%',
        width: '500px',
        height: '2px',
        borderTop: '2px dotted #3b82f6',
        opacity: 0.6
      }}
    />
  );

  return (
    <section 
      id="features" 
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="py-16 lg:py-24 relative overflow-hidden bg-slate-50/30 group/features"
    >
      {/* Elementos Decorativos de Fundo - Sutis */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-slate-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-200/20 rounded-full blur-3xl" />
        
        {/* Grid Base - Mais visível quando apagado */}
        <div className="absolute inset-0 opacity-[0.04]" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px' 
          }} 
        />

        {/* Grid Hover - Mais suave e com mais blur */}
        <div className="absolute inset-0 opacity-0 group-hover/features:opacity-[0.12] transition-opacity duration-700" 
          style={{ 
            backgroundImage: `
              linear-gradient(to right, #3b82f6 1px, transparent 1px),
              linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
            `, 
            backgroundSize: '40px 40px',
            WebkitMaskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
            maskImage: `radial-gradient(circle 350px at ${mousePos.x}px ${mousePos.y}px, black 0%, transparent 100%)`,
          }} 
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-blue-200 rounded-full text-sm font-medium text-gray-700 mb-4">
            <Sparkles className="w-4 h-4 mr-2 text-blue-600" />
            5 Superpoderes Operacionais
          </div>
          <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-4">
            Onflow: Telefonia + Operação + Inteligência
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            A solução completa para times que precisam de escala, controle e qualidade em cada ligação.
          </p>
        </div>

        {/* Features List */}
        <div className="space-y-16">
          {features.map((feature, index) => {
            const isReversed = index % 2 === 1;
            return (
              <div 
                key={feature.id} 
                className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-16 relative ${
                  isReversed ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className="w-full lg:flex-1 max-w-2xl lg:max-w-md text-center lg:text-left relative">
                  <div className="mb-6 lg:mb-8">
                    <div className={`${!isReversed ? 'lg:text-right' : ''}`}>
                      <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-xl text-base font-bold mb-4 lg:mb-6 relative">
                        {feature.id}
                        {/* Connection Line from Number */}
                        <ConnectionLine isReversed={isReversed} />
                      </span>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>

                  {/* Benefits List */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-4">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center space-x-3 justify-center lg:justify-start bg-white lg:bg-transparent p-3 lg:p-0 rounded-xl border border-gray-100 lg:border-none shadow-sm lg:shadow-none">
                        <div className="flex-shrink-0">
                          <Check className="w-5 h-5 text-blue-500" />
                        </div>
                        <span className="text-sm lg:text-base text-gray-700 font-medium lg:font-normal">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Video Card */}
                <div className="w-full lg:flex-1 flex justify-center items-center">
                  <VideoCard title={feature.title} id={feature.id} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features; 
